import IconPrev from "@/assets/images/arrow-back.png";
import IconPrevDisable from "@/assets/images/arrow-disable-back.png";
import IconNextDisable from "@/assets/images/arrow-disable-forward.png";
import IconNext from "@/assets/images/arrow-forward.png";
import PinningModal, { PINNING_MODAL_CONTAINER } from "@/components/common/pinning-modal/pinning-modal";
import { TeacherClassLessonPlan } from "@/locales/localeid";
// import { TeacherRoomManager } from "@/manager/room/teacher.manager";
import { Exposure, ExposureItemMedia, ExposureType } from "@/store/lesson/state";
import { NEXT_EXPOSURE, PREV_EXPOSURE } from "@/utils/constant";
import { getSeconds, secondsToTimeStr } from "@/utils/convertDuration";
import { CloseOutlined, LoadingOutlined, PushpinOutlined } from "@ant-design/icons-vue";
import { Badge, Empty } from "ant-design-vue";
import { debounce } from "lodash";
import { computed, defineComponent, onMounted, onUnmounted, provide, ref, watch, nextTick } from "vue";
import { fmtMsg } from "vue-glcommonui";
import { useStore } from "vuex";
import ExposureDetail from "./exposure-detail/exposure-detail.vue";
import LessonActivity from "./lesson-activity/lesson-activity.vue";
import { ChevronLeftIcon, InformationCircleIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import { useTeachingMode } from "@/hooks/use-class-view";
import { isAntdModalOpen, isAntdSelectOpen, isInputBeingEdited } from "@/utils/utils";
import { TeachingMode } from "@/models";
import { MODULES, vuexName } from "@/store/utils";

export enum ExposureDetailType {
  TRANSITION_BLOCK = "TRANSITION_BLOCK",
  LP_COMPLETE_BLOCK = "LP_COMPLETE_BLOCK",
  VCP_BLOCK = "VPC_BLOCK",
  CONTENT_BLOCK = "CONTENT_BLOCK",
  TEACHING_ACTIVITY_BLOCK = "TEACHING_ACTIVITY_BLOCK",
  ALTERNATE_MEDIA_BLOCK = "ALTERNATE_MEDIA_BLOCK",
}

export const getExposureFirstSlide = (exposure: Exposure, includingAlternative: boolean): ExposureItemMedia | undefined =>
  [
    ...exposure.items,
    ...exposure.contentBlockItems,
    ...exposure.teachingActivityBlockItems,
    ...(includingAlternative ? exposure.alternateMediaBlockItems.flat() : []),
  ].filter((item) => item.media[0]?.image?.url)[0]?.media[0];

const INFO_BUTTON_ID = "lp-info";

export enum PopupStatus {
  Pinned = "Pinned",
  Showed = "Showed",
  Hided = "Hided",
}

export default defineComponent({
  components: {
    LessonActivity,
    ExposureDetail,
    Empty,
    Badge,
    PushpinOutlined,
    CloseOutlined,
    LoadingOutlined,
    ChevronLeftIcon,
    InformationCircleIcon,
    ChevronRightIcon,
    // LoadingIcon,
  },
  props: ["isHelper"],
  setup(props) {
    const { getters, dispatch, commit } = useStore();
    const { isLpSidebarShrink } = useTeachingMode();
    const canSendRealtimeAction = computed<boolean>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CAN_SEND_REALTIME_ACTION)]);
    const isForceShrinkSidebar = computed(() => !props.isHelper && !canSendRealtimeAction.value);
    const isSidebarShrink = computed(() => isLpSidebarShrink.value || isForceShrinkSidebar.value);
    const isSidebarToggleDisabled = computed(() => props.isHelper || isForceShrinkSidebar.value);
    const exposures = computed<Exposure[]>(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_EXPOSURES)]);
    const activityStatistic = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_ACTIVITY_STATISTIC)]);
    const currentExposure = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)]);
    const currentLesson = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CURRENT_LESSON)]);
    const currentUnit = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CURRENT_UNIT)]);
    const progress = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_PROGRESS_STATISTIC)]);
    const remainingTime = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_REMAINING_TIME_STATISTIC)]);
    const currentExposureItemMedia = computed<ExposureItemMedia | undefined>(
      () => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)],
    );
    const nextExposureItemMedia = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_NEXT_EXPOSURE_ITEM_MEDIA)]);
    const prevExposureItemMedia = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_PREV_EXPOSURE_ITEM_MEDIA)]);
    const page = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_PAGE)]);
    const isIndependentMode = computed<boolean>(() => getters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_IS_INDEPENDENT_MODE)]);
    const nextCurrentExposure = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_NEXT_EXPOSURE)]);
    const prevCurrentExposure = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_PREVIOUS_EXPOSURE)]);
    const infoPopupStatus = ref<PopupStatus>(PopupStatus.Hided);
    const infoIconRef = ref();
    const canNext = computed(() => !!(nextExposureItemMedia.value || nextCurrentExposure.value));
    const canPrev = computed(() => !!(prevExposureItemMedia.value || prevCurrentExposure.value));
    const iconNext = computed(() => (canNext.value ? IconNext : IconNextDisable));
    const iconPrev = computed(() => (canPrev.value ? IconPrev : IconPrevDisable));
    const exposureTitle = computed(() => {
      const exposure = getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)];
      if (!exposure) return "";
      switch (exposure.type) {
        case ExposureType.TRANSITION:
          return transitionText.value;
        case ExposureType.COMPLETE:
          return lessonCompleteText.value;
        default:
          return `${exposure.name} (${secondsToTimeStr(getSeconds(exposure.duration))})`;
      }
    });
    const teachingIconPosition = ref({ left: 0, top: 0 });
    const lessonContainer = ref();
    const scrollPosition = ref(0);
    const showInfo = ref(false);
    const isTransitionBlock = computed(() => currentExposure.value?.type === ExposureType.TRANSITION);
    const teachingContentEmpty = computed(() => {
      return currentExposure.value?.teachingActivityBlockItems?.findIndex((teachingItem: any) => teachingItem.textContent) <= -1;
    });
    const isOneOneMode = ref("");
    const oneToOneStudentId = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]);
    watch(oneToOneStudentId, (value) => {
      if (value === "" || value === null) {
        isOneOneMode.value = "";
      } else {
        isOneOneMode.value = value;
      }
      infoPopupStatus.value = PopupStatus.Hided;
    });

    const toggleLpSidebar = async () => {
      if (isSidebarToggleDisabled.value) return;
      const mode = isSidebarShrink.value ? TeachingMode.OpenLessonPlan : TeachingMode.CloseLessonPlan;
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHING_MODE_ASYNC), mode);
    };

    const onClickExposure = async (exposure: Exposure | null) => {
      if (!exposure || exposure.id === currentExposure.value?.id) return;
      const exposureFirstSlide = getExposureFirstSlide(exposure, isAlternateMediaShown.value);
      /** Reset whiteboard's state and update new item in local */
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.CHANGE_LOCAL_EXPOSURE_ITEM), {
        exposureId: exposure.id,
        exposureItemId: exposureFirstSlide?.id ?? "",
        exposureType: exposureFirstSlide?.exposureType,
      });
      /** Then send the message to students and helper */
      await updateToRemoteUsers(exposure.id, exposureFirstSlide?.id ?? "");
    };

    const handleClickCloseExposure = async () => {
      updateToRemoteUsers.cancel();
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.CHANGE_LOCAL_EXPOSURE_ITEM), { exposureItemId: "" });
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.REQUEST_CHANGE_EXPOSURE_ITEM), { exposureItemId: "" });
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.END_EXPOSURE), { id: currentExposure.value?.id });
      commit(vuexName(MODULES.LESSON.COMMITS.END_CURRENT_CONTENT));
    };

    const checkIdpModeBeforeChangeExposure = async (callback: () => Promise<void>) => {
      if (isIndependentMode.value && !oneToOneStudentId.value) {
        await dispatch(vuexName(MODULES.MODAL.DISPATCHES.WARNING_IDP_MODE_BEFORE_EXECUTE), callback);
      } else {
        await callback();
      }
    };

    const onClickCloseExposure = async () => {
      if (props.isHelper) {
        return;
      }
      await checkIdpModeBeforeChangeExposure(handleClickCloseExposure);
    };

    const onClickPrevNextMedia = async (nextOrBack: number) => {
      if (isSidebarShrink.value || props.isHelper) return;
      const scrollLimitPosition = Math.max(
        document.body.scrollHeight,
        lessonContainer.value.scrollHeight,
        document.body.offsetHeight,
        lessonContainer.value.offsetHeight,
        document.body.clientHeight,
        lessonContainer.value.clientHeight,
      );
      scrollPosition.value = lessonContainer.value.scrollTop;
      //* click next
      if (nextOrBack === NEXT_EXPOSURE) {
        if (!canNext.value) return;
        if (nextExposureItemMedia.value !== undefined) {
          await handleLessonItemChange(nextExposureItemMedia.value.id, nextExposureItemMedia.value.exposureType);
          scrollPosition.value = scrollPosition.value < scrollLimitPosition ? scrollPosition.value + 50 : scrollLimitPosition;
        } else {
          await checkIdpModeBeforeChangeExposure(async () => {
            await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.END_EXPOSURE), { id: currentExposure?.value?.id });
            await onClickExposure(nextCurrentExposure.value);
            scrollPosition.value = 0;
          });
        }
        lessonContainer.value.scrollTo(0, scrollPosition.value);
      } else {
        //* click back
        if (!canPrev.value) return;
        if (prevExposureItemMedia.value !== undefined) {
          await handleLessonItemChange(prevExposureItemMedia.value.id, prevExposureItemMedia.value.exposureType);
          scrollPosition.value = scrollPosition.value <= 0 ? 0 : scrollPosition.value - 50;
        } else {
          await checkIdpModeBeforeChangeExposure(async () => {
            await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.END_EXPOSURE), { id: currentExposure?.value?.id });
            await onClickExposure(prevCurrentExposure.value);
            scrollPosition.value = 0;
          });
        }
        lessonContainer.value.scrollTo(0, scrollPosition.value);
      }
    };

    const isShowExposureDetail = computed(() => {
      const exposure = getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)];
      return exposure !== undefined;
    });

    watch(isShowExposureDetail, (val) => {
      if (!val) {
        infoPopupStatus.value = PopupStatus.Hided;
      }
    });
    const isTransitionType = computed(() => {
      const exposure = getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)];
      return exposure.type === ExposureType.TRANSITION;
    });

    const isCompleteType = computed(() => {
      const exposure = getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)];
      return exposure.type === ExposureType.COMPLETE;
    });
    const handleKeyDown = async (e: any) => {
      if (isAntdModalOpen() || isAntdSelectOpen() || isInputBeingEdited()) {
        return;
      }
      if (e.key == "ArrowRight" || e.key == "ArrowDown") {
        e.preventDefault();
        await onClickPrevNextMedia(NEXT_EXPOSURE);
      } else if (e.key == "ArrowLeft" || e.key == "ArrowUp") {
        e.preventDefault();
        await onClickPrevNextMedia(PREV_EXPOSURE);
      }
    };

    const hasLongShortcutHeader = computed(() => {
      return currentLesson.value >= 10 || currentUnit.value >= 10;
    });

    const handleMouseMove = debounce(() => {
      if (infoPopupStatus.value === PopupStatus.Pinned) return;
      const editableModalEl = document.querySelector<HTMLElement>(`.${PINNING_MODAL_CONTAINER}`);
      if (!editableModalEl) return;
      const infoButtonEl = document.getElementById(INFO_BUTTON_ID);
      if (!editableModalEl || !infoButtonEl) {
        return (infoPopupStatus.value = PopupStatus.Hided);
      }
      const finalModalHovered = editableModalEl.matches(":hover");
      const noteInfoTriggerHovered = infoButtonEl.matches(":hover");
      if (!noteInfoTriggerHovered && !finalModalHovered) {
        infoPopupStatus.value = PopupStatus.Hided;
      }
    }, 10);
    onMounted(() => {
      window.addEventListener("keydown", handleKeyDown);
    });

    onUnmounted(() => {
      window.removeEventListener("keydown", handleKeyDown);
    });

    const handleMouseOver = () => {
      if (infoPopupStatus.value === PopupStatus.Pinned) return;
      teachingIconPosition.value = {
        left: infoIconRef.value.getBoundingClientRect().left,
        top: infoIconRef.value.getBoundingClientRect().top,
      };
      infoPopupStatus.value = PopupStatus.Showed;
    };
    watch(infoPopupStatus, (val) => {
      if (val === PopupStatus.Showed) {
        window.addEventListener("mousemove", handleMouseMove);
      }
      if (val === PopupStatus.Hided || val === PopupStatus.Pinned) {
        window.removeEventListener("mousemove", handleMouseMove);
      }
    });
    const editableModalRef = ref<InstanceType<typeof PinningModal>>();
    const handlePinOrHide = (status: PopupStatus) => {
      infoPopupStatus.value = status;
    };
    const visible = computed(() => infoPopupStatus.value === PopupStatus.Showed);

    const updateToRemoteUsers = debounce(async (exposureId: string, exposureItemId: string) => {
      //* Update new exposure item and clear the whiteboard in to session
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.REQUEST_CHANGE_EXPOSURE_ITEM), { exposureId, exposureItemId });
    }, 100);
    const handleLessonItemChange = async (exposureItemId: string, exposureType?: ExposureDetailType) => {
      /** Reset whiteboard's state and update new item in local */
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.CHANGE_LOCAL_EXPOSURE_ITEM), { exposureItemId, exposureType });
      /** Then send the message to students and helper */
      await updateToRemoteUsers(currentExposure.value?.id ?? "", exposureItemId);
    };
    const autoScrollLessonContainer = () => {
      const activeItem: HTMLElement | null = document.querySelector(".item-media.item-active");
      if (activeItem) {
        const isActiveElementScrolledPast = activeItem.offsetTop < lessonContainer.value.scrollTop;
        const isActiveElementDownBelow =
          activeItem.offsetTop + activeItem.offsetHeight > lessonContainer.value.scrollTop + lessonContainer.value.offsetHeight;
        if (isActiveElementDownBelow || isActiveElementScrolledPast) {
          lessonContainer.value.scrollTo(0, activeItem.offsetTop);
        }
      }
    };
    watch(currentExposureItemMedia, (val) => {
      if (val && props.isHelper) {
        nextTick().then(autoScrollLessonContainer);
      }
    });
    const isAlternateMediaShown = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_IS_ALTERNATE_MEDIA_SHOWN)]);
    provide("lesson-item-change", handleLessonItemChange);

    const unitText = computed(() => fmtMsg(TeacherClassLessonPlan.Unit));
    const lessonText = computed(() => fmtMsg(TeacherClassLessonPlan.Lesson));
    const remainingText = computed(() => fmtMsg(TeacherClassLessonPlan.Remaining));
    const itemText = computed(() => fmtMsg(TeacherClassLessonPlan.Item));
    const noDataText = computed(() => fmtMsg(TeacherClassLessonPlan.NoData));
    const pageText = computed(() => fmtMsg(TeacherClassLessonPlan.Page));
    const transitionText = computed(() => fmtMsg(TeacherClassLessonPlan.Transition));
    const lessonCompleteText = computed(() => fmtMsg(TeacherClassLessonPlan.LessonComplete));

    return {
      isLpSidebarShrink,
      canSendRealtimeAction,
      isSidebarShrink,
      isSidebarToggleDisabled,
      exposures,
      currentExposure,
      progress,
      remainingTime,
      isShowExposureDetail,
      isTransitionType,
      isCompleteType,
      activityStatistic,
      onClickExposure,
      onClickCloseExposure,
      toggleLpSidebar,
      page,
      onClickPrevNextMedia,
      nextExposureItemMedia,
      iconNext,
      iconPrev,
      NEXT_EXPOSURE,
      PREV_EXPOSURE,
      exposureTypes: ExposureDetailType,
      currentLesson,
      currentUnit,
      unitText,
      lessonText,
      remainingText,
      itemText,
      pageText,
      lessonContainer,
      isOneOneMode,
      exposureTitle,
      showInfo,
      teachingContentEmpty,
      isTransitionBlock,
      hasLongShortcutHeader,
      editableModalRef,
      infoIconRef,
      teachingIconPosition,
      handleMouseOver,
      visible,
      handlePinOrHide,
      infoPopupStatus,
      PopupStatus,
      noDataText,
      canNext,
      canPrev,
      isAlternateMediaShown,
      ChevronLeftIcon,
      ChevronRightIcon,
    };
  },
});
